import {
  collection,
  addDoc,
  doc,
  query,
  where,
  orderBy,
  getDocs,
  serverTimestamp,
  WriteBatch,
} from 'firebase/firestore';
import { db, auth } from '../../firebaseConfig';
import { generateUniqueId } from '../../utils/idGenerator';
import { COLLECTIONS } from './constants';
import { AuditLog, DocumentRevision } from '../../types/workflowTypes';
import { getCurrentTimestamp } from './utils/dataUtils';
import { getEffectiveScopeUid } from '../../utils/tenantScope';

/**
 * Service for audit logging and document revision tracking
 */
export class AuditService {
  /**
   * Adds an audit log entry
   * @param userId - User ID
   * @param actionType - Type of action
   * @param targetType - Target object type
   * @param targetId - ID of the target object
   * @param details - Action details
   * @param workflowReferenceId - Optional workflow reference ID
   * @returns ID of the created audit log entry
   */
  static async addAuditLog(
    userId: string,
    actionType: string,
    targetType: string,
    targetId: string,
    details: any,
    workflowReferenceId?: string
  ): Promise<string> {
    const logId = generateUniqueId();
    const now = getCurrentTimestamp();
    
    const auditLogData: AuditLog = {
      logId,
      userId,
      timestamp: now,
      action: actionType,
      targetType,
      targetId,
      details,
      userAgent: navigator.userAgent
    };
    
    if (workflowReferenceId) {
      auditLogData.workflowReferenceId = workflowReferenceId;
    }
    
    const collectionRef = collection(db, COLLECTIONS.AUDIT_LOGS);
    await addDoc(collectionRef, {
      ...auditLogData,
      timestamp: serverTimestamp()
    });

    return logId;
  }

  /**
   * Appends an audit-log entry to an EXISTING `writeBatch` instead of
   * committing it standalone (additive companion to {@link addAuditLog}, BR-6 /
   * architecture-bank-reconciliation.md §9.3, §19).
   *
   * Motivation (Coder 🔴-1 / GoBD): the Bank-Abgleich payment writes
   * (`bankApi.confirmIncomingMatch`/`unlinkTransaction`/`confirmPaymentReversal`)
   * bypass `paymentsApi.createPayment` because they need Firestore
   * batch-atomicity (payment doc + `bank_transactions` update in ONE commit) —
   * which `createPayment`'s single `addDoc` cannot provide. That bypass would
   * also skip the M9a audit trail. This method lets those batches write the
   * SAME audit-log document shape as {@link addAuditLog} (same
   * `COLLECTIONS.AUDIT_LOGS`, same fields, server-authoritative
   * `serverTimestamp()`), but into the caller's batch so the audit entry
   * commits atomically WITH the payment mutation — never partially.
   *
   * {@link addAuditLog} is intentionally left byte-identical (M9a contract —
   * `paymentsApi.m9a.test.ts` / `expensesApi.m9a.test.ts` must stay green).
   * This is a brand-new, self-contained method; nothing about the existing
   * standalone path changes.
   *
   * @param batch               - The open `writeBatch` to append the log to.
   * @param userId              - Owner UID (sets `userId` so `ownsIncoming()` passes).
   * @param actionType          - Action, e.g. `'create'` / `'cancel'`.
   * @param targetType          - Target object type, e.g. `'payment'`.
   * @param targetId            - Firestore document ID of the affected object.
   * @param details             - Additional context merged into `details`.
   * @param workflowReferenceId - Optional workflow reference ID.
   * @returns The generated `logId` (the Firestore doc ID is auto-allocated).
   */
  static appendAuditLogToBatch(
    batch: WriteBatch,
    userId: string,
    actionType: string,
    targetType: string,
    targetId: string,
    details: any,
    workflowReferenceId?: string
  ): string {
    const logId = generateUniqueId();
    const now = getCurrentTimestamp();

    const auditLogData: AuditLog = {
      logId,
      userId,
      timestamp: now,
      action: actionType,
      targetType,
      targetId,
      details,
      userAgent: navigator.userAgent
    };

    if (workflowReferenceId) {
      auditLogData.workflowReferenceId = workflowReferenceId;
    }

    const docRef = doc(collection(db, COLLECTIONS.AUDIT_LOGS));
    batch.set(docRef, {
      ...auditLogData,
      timestamp: serverTimestamp()
    });

    return logId;
  }

  /**
   * Adds a document revision entry
   * @param documentId - Document ID
   * @param documentType - Document type
   * @param changeReason - Reason for the change
   * @param changes - Description of changes
   * @returns ID of the created revision
   */
  static async addDocumentRevision(
    documentId: string,
    documentType: 'invoice' | 'offer' | 'other',
    changeReason: string,
    changes: { field: string; oldValue: any; newValue: any }[]
  ): Promise<string> {
    if (!auth.currentUser) {
      throw new Error('Access denied: No authenticated user found');
    }
    
    const revisionId = generateUniqueId();
    const userId = getEffectiveScopeUid();
    const now = getCurrentTimestamp();
    
    const revisionData: DocumentRevision = {
      revisionId,
      documentId,
      documentType,
      userId,
      timestamp: now,
      changeReason,
      changes,
      ipAddress: undefined, // Will be added server-side
      userAgent: navigator.userAgent
    };
    
    const collectionRef = collection(db, COLLECTIONS.DOCUMENT_REVISIONS);
    await addDoc(collectionRef, {
      ...revisionData,
      timestamp: serverTimestamp()
    });
    
    return revisionId;
  }

  /**
   * Gets document revisions
   * @param documentId - Document ID
   * @param options - Additional options like limit and sorting
   * @returns List of document revisions
   */
  static async getDocumentRevisions(
    documentId: string,
    options: {
      limit?: number,
      orderDirection?: 'asc' | 'desc'
    } = {}
  ): Promise<DocumentRevision[]> {
    const collectionRef = collection(db, COLLECTIONS.DOCUMENT_REVISIONS);
    
    // Create base query
    let q = query(collectionRef, where('documentId', '==', documentId));
    
    // Sorting
    const orderDirection = options.orderDirection || 'desc';
    q = query(q, orderBy('timestamp', orderDirection));
    
    // Limit
    if (options.limit) {
      q = query(q, orderBy('timestamp', orderDirection));
    }
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => doc.data() as DocumentRevision);
  }

  /**
   * Gets audit logs for a specific workflow
   * @param workflowReferenceId - Workflow reference ID
   * @param options - Additional options like limit and sorting
   * @returns List of audit logs
   */
  static async getAuditLogs(
    workflowReferenceId: string,
    options: {
      limit?: number,
      orderDirection?: 'asc' | 'desc',
      actionType?: string,
      targetType?: string
    } = {}
  ): Promise<AuditLog[]> {
    const collectionRef = collection(db, COLLECTIONS.AUDIT_LOGS);
    
    // Create base query
    let q = query(collectionRef, where('workflowReferenceId', '==', workflowReferenceId));
    
    // Filter by action type, if specified
    if (options.actionType) {
      q = query(q, where('actionType', '==', options.actionType));
    }
    
    // Filter by target object type, if specified
    if (options.targetType) {
      q = query(q, where('targetType', '==', options.targetType));
    }
    
    // Sorting
    const orderDirection = options.orderDirection || 'desc';
    q = query(q, orderBy('timestamp', orderDirection));
    
    // Limit
    if (options.limit) {
      q = query(q, orderBy('timestamp', orderDirection));
    }
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => doc.data() as AuditLog);
  }
} 